'use client'

import { useCallback, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

const CONSENT_KEY = 'kk_consent'
const AID_COOKIE = 'kk_aid'
const AID_MAX_AGE = 60 * 60 * 24 * 396 // ~13 months

function getConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

function ensureAid() {
  const match = document.cookie.match(/(?:^|;\s*)kk_aid=([^;]+)/)
  if (match) return match[1]
  const aid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`
  document.cookie = `${AID_COOKIE}=${aid}; Max-Age=${AID_MAX_AGE}; Path=/; SameSite=Lax`
  return aid
}

function currentUtm() {
  const p = new URLSearchParams(window.location.search)
  const utm = {}
  for (const k of ['source', 'medium', 'campaign', 'term', 'content']) {
    const v = p.get(`utm_${k}`)
    if (v) utm[k] = v
  }
  return utm
}

function sendPageview() {
  const payload = JSON.stringify({
    type: 'pageview',
    url: window.location.pathname + window.location.search,
    pageTitle: document.title,
    utm: currentUtm(),
  })
  // sendBeacon survives navigation; fetch keepalive is the fallback.
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      '/api/t',
      new Blob([payload], { type: 'application/json' }),
    )
  } else {
    fetch('/api/t', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {})
  }
}

// Consent-gated first-party analytics: a small banner on first visit; once
// accepted, a kk_aid cookie identifies the browser and pageviews log to /api/t
// (stitched onto the CRM contact when the visitor identifies themselves).
export function ConsentTracker() {
  const pathname = usePathname()
  const [status, setStatus] = useState('unknown') // unknown | ask | granted | denied

  useEffect(() => {
    const stored = getConsent()
    if (stored === 'granted') setStatus('granted')
    else if (stored === 'denied') setStatus('denied')
    else setStatus('ask')
  }, [])

  // Log a pageview on every route change while consent is granted.
  useEffect(() => {
    if (status !== 'granted') return
    ensureAid()
    sendPageview()
  }, [status, pathname])

  const decide = useCallback((value) => {
    try {
      localStorage.setItem(CONSENT_KEY, value)
    } catch {
      /* private mode — session-only consent */
    }
    setStatus(value)
  }, [])

  if (status !== 'ask') return null

  return (
    <div className="fixed bottom-4 left-4 z-[90] max-w-sm rounded-2xl border border-white/10 bg-neutral-950/95 p-4 text-white shadow-[0_24px_60px_rgba(0,0,0,0.45)] backdrop-blur-md">
      <p className="text-sm leading-6 text-white/80">
        We use a first-party cookie to understand what&apos;s working on our
        site and improve your experience. No data is sold.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => decide('granted')}
          className="rounded-full bg-kingdom-gold px-4 py-2 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65]"
        >
          Accept
        </button>
        <button
          type="button"
          onClick={() => decide('denied')}
          className="rounded-full px-4 py-2 text-sm font-semibold text-white/60 transition hover:text-white"
        >
          Decline
        </button>
      </div>
    </div>
  )
}
